import { ChangeEvent, useState } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import { Button } from "../base/Button";
import usePinnacleMappingMutations from "../../data/PinnacleMapping/usePinnacleMappingMutations";
import { parseMatchSheet } from "./pinnacleMeta";

/**
 * One-off and occasional jobs: bring the old Pinnacle names across as mappings to check, load the
 * match sheet as suggestions, suggest by name, and add an item type by hand. Each is safe to repeat.
 */
export function SetupTools() {
  const { importLegacy, importMatchSheet, buildSuggestions, createItemType } = usePinnacleMappingMutations();
  const [newName, setNewName] = useState("");
  const [sheetError, setSheetError] = useState<string | null>(null);

  const onSheet = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const rows = parseMatchSheet(await file.text());

    if (rows.length === 0) {
      setSheetError(`${file.name} has no rows with an Id and a pinnacle_name column.`);
      return;
    }

    setSheetError(null);
    importMatchSheet.mutate(rows);
  };

  return (
    <Disclosure as="section" className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <DisclosureButton className="group flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700">
        Set up and import
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-data-[open]:rotate-180" aria-hidden />
      </DisclosureButton>
      <DisclosurePanel className="grid gap-4 border-t border-gray-100 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2">
          <p className="font-medium text-gray-900">Old Pinnacle names</p>
          <p className="text-gray-500">
            Copies each category's Pinnacle name across as a mapping marked legacy, so imports keep working as they do today. Do this
            before the first push.
          </p>
          <Button variant="outlined" size="sm" onClick={() => importLegacy.mutate()} disabled={importLegacy.isPending}>
            Import legacy names
          </Button>
        </div>

        <div className="space-y-2">
          <p className="font-medium text-gray-900">Match sheet</p>
          <p className="text-gray-500">
            Loads partcategory_pinnacle_matches.csv. Each row becomes a suggestion with the sheet's confidence.
          </p>
          <label className="inline-block cursor-pointer rounded border border-orange-600 px-2 py-1 text-sm text-gray-700 hover:bg-orange-600/20">
            {importMatchSheet.isPending ? "Importing…" : "Choose CSV"}
            <input id="pinnacle-match-sheet" type="file" accept=".csv,text/csv" className="sr-only" onChange={onSheet} />
          </label>
          {sheetError && <p className="text-xs text-red-600">{sheetError}</p>}
        </div>

        <div className="space-y-2">
          <p className="font-medium text-gray-900">Suggest by name</p>
          <p className="text-gray-500">
            Suggests categories whose names contain the item type's words, with LH/RH and front/rear taken from the category.
          </p>
          <Button variant="outlined" size="sm" onClick={() => buildSuggestions.mutate()} disabled={buildSuggestions.isPending}>
            Build suggestions
          </Button>
        </div>

        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createItemType.mutate(newName.trim(), { onSuccess: () => setNewName("") });
          }}
        >
          <label htmlFor="pinnacle-new-item-type" className="block font-medium text-gray-900">
            Add an item type
          </label>
          <p className="text-gray-500">Item types also appear on their own once a customer imports a part of one.</p>
          <div className="flex gap-2">
            <input
              id="pinnacle-new-item-type"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. DOOR_MIRROR"
              className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 font-mono text-sm"
            />
            <Button type="submit" size="sm" disabled={!newName.trim() || createItemType.isPending}>
              Add
            </Button>
          </div>
        </form>
      </DisclosurePanel>
    </Disclosure>
  );
}
